export interface Admin
 {
  id              : number ,
  email           : string ,
  lastname        : string ,
  firstname       : string ,
  role            : string ,
  profileRole     : string , // 'utilisateur' | 'createur' | 'editeur'
  pseudonym       : string , // nom public
  slug            : string , // dérivé du pseudonyme, affiché en liste sous la forme "@slug"
  biography       : string ,
  personalPageUrl : string ,
  reporterAvailable : boolean, // accepte d'être sollicité comme reporter Kleon pour couvrir un événement
  reporterCity      : string,  // ville de départ du reporter
  reporterRadiusKm  : number | null, // rayon de déplacement du reporter, en km
  verified        : boolean, // créateur vérifié
  accountStatus   : string , // statut du compte, indépendant de "verified" : 'active' | 'pending' | 'suspended' | 'disabled'
  useFiligrane    : boolean, // filigrane appliqué sur les diffusions/enregistrements de ce créateur
  durationMaxBroadcasting     : number, // quota de durée cumulée de diffusion, en secondes
  durationElapsedBroadcasting : number, // durée de diffusion déjà consommée, en secondes
  broadcastSeconds            : number, // cumul des diffusions (chaîne allumée), en secondes, depuis le 11/10/2026 — lecture seule, liste des créateurs
  broadcastSessions           : number, // nombre de diffusions (allumages de chaîne) sur la même période — lecture seule
  durationMaxRecording        : number, // quota de durée cumulée d'enregistrement, en secondes
  durationElapsedRecording    : number, // durée d'enregistrement déjà consommée, en secondes

  // Remise à zéro périodique des quotas de durée (uniquement significatif en mode "limité",
  // cf. estDureeSansLimite dans quota.ts). ResetDay : jour du mois (1-31) si 'monthly', jour
  // de la semaine ISO (1=lundi..7=dimanche) si 'weekly', ignoré si 'daily'. ResetTime : heure
  // de prise en compte, en secondes depuis minuit (0-86399).
  durationMaxBroadcastingResetFrequency : 'daily' | 'weekly' | 'monthly',
  durationMaxBroadcastingResetDay       : number,
  durationMaxBroadcastingResetTime      : number,
  durationMaxRecordingResetFrequency    : 'daily' | 'weekly' | 'monthly',
  durationMaxRecordingResetDay          : number,
  durationMaxRecordingResetTime         : number,
  nbSubscribers   : number , // nombre d'abonnés
  lastLoginDate   : Date | null,
  lastLoginIP     : string,
  lastLoginCity   : string,
  lastLoginDevice : string,
  quotaDisk       : number, // quota d'espace disque alloué, en octets
  quotaUsed       : number, // espace disque actuellement utilisé, en octets
  nbDevicesMax    : number, // nombre maximum d'appareils autorisés
  nbDevicesUsed   : number, // nombre d'appareils actuellement utilisés
  nbChannelsMax   : number, // nombre maximum de chaînes de diffusion autorisées
  nbChannelsUsed  : number, // nombre de chaînes de diffusion actuellement utilisées
  uuid            : string, // UUID public — même mécanique qu'un media : nomme files/public/{uuid}/images/ (profil.webp, banner.webp) sur le serveur média ('' si aucun)
  publicRef       : string,
  photoFile       : string, // nom du fichier privé de la photo de profil ('' si aucune)
  bannerFile      : string, // nom du fichier privé de l'image de bandeau ('' si aucune)
  address         : string,
  zipCode         : string,
  city            : string,
  region          : string,
  country         : string,
  creationDate          : Date | null,
  lastModificationDate  : Date | null,
 }

// Valeurs stockées / libellés affichés du rôle de profil
export const PROFILE_ROLE_VALUES = [ 'utilisateur' , 'createur' , 'editeur' ];
export const PROFILE_ROLE_LABELS = [ 'Utilisateur' , 'Créateur'  , 'Éditeur' ];

// Valeurs stockées / libellés affichés du statut de compte (indépendant de "verified")
export const ACCOUNT_STATUS_VALUES = [ 'active' , 'pending'    , 'suspended' , 'disabled'  ];
export const ACCOUNT_STATUS_LABELS = [ 'Actif'  , 'En attente' , 'Suspendu'  , 'Désactivé' ];

export interface Authentication
 {
  login            : string ,
  token            : string ,
  numberOfAttempts : number ,
  admin            : Admin | null
 }

export const initializedAdmin = (): Admin => (
 {
  id              : 0  ,
  email           : '' ,
  lastname        : '' ,
  firstname       : '' ,
  role            : '' ,
  profileRole     : 'utilisateur',
  pseudonym       : '' ,
  slug            : '' ,
  biography       : '' ,
  personalPageUrl : '' ,
  reporterAvailable : false,
  reporterCity      : '',
  reporterRadiusKm  : null,
  verified        : false,
  accountStatus   : 'active',
  useFiligrane    : false,
  durationMaxBroadcasting     : 3600,
  durationElapsedBroadcasting : 0,
  broadcastSeconds            : 0,
  broadcastSessions           : 0,
  durationMaxRecording        : 3600,
  durationElapsedRecording    : 0,
  durationMaxBroadcastingResetFrequency : 'monthly',
  durationMaxBroadcastingResetDay       : 1,
  durationMaxBroadcastingResetTime      : 0,
  durationMaxRecordingResetFrequency    : 'monthly',
  durationMaxRecordingResetDay          : 1,
  durationMaxRecordingResetTime         : 0,
  nbSubscribers   : 0,
  lastLoginDate   : null,
  lastLoginIP     : '',
  lastLoginCity   : '',
  lastLoginDevice : '',
  quotaDisk       : 0,
  quotaUsed       : 0,
  nbDevicesMax    : 0,
  nbDevicesUsed   : 0,
  nbChannelsMax   : 0,
  nbChannelsUsed  : 0,
  uuid            : '',
  publicRef       : '',
  photoFile       : '',
  bannerFile      : '',
  address         : '',
  zipCode         : '',
  city            : '',
  region          : '',
  country         : '',
  creationDate          : null,
  lastModificationDate  : null,
});

export const initializedAuthentication = (): Authentication => (
 {
  login            : ''   ,
  token            : ''   ,
  numberOfAttempts : 0    ,
  admin            : null
});
